import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  RotateCcw,
  Settings,
  LogOut,
  ChevronRight,
  Menu,
  X,
  Factory,
  Users,
  DollarSign,
  Cog,
  Wrench,
} from "lucide-react";
import Logo from "@/app/icons/Logo";
import { cn } from "@/lib/utils";
import { AppDrawer } from "@/components/app/AppDrawer";
import { LogoutDialog } from "./LogoutDialog";
import { useSidebar } from "@/components/app/SidebarContext";
import { ROUTES } from "@/lib/routes";
import { authService } from "@/api/auth";
import { useAuthStore } from "@/store/useAuthStore";

const navItems = [
  { label: "Dashboard", href: ROUTES.DASHBOARD.HOME, icon: LayoutDashboard },
  {
    label: "Orders",
    href: ROUTES.DASHBOARD.ORDER_MANAGEMENT,
    icon: ShoppingCart,
  },
  {
    label: "Repairs",
    href: ROUTES.DASHBOARD.REPAIR_MANAGEMENT,
    icon: Wrench,
  },
  {
    label: "Product",
    href: ROUTES.DASHBOARD.PRODUCT_MANAGEMENT,
    icon: Package,
  },
  { label: "Manufactures", href: ROUTES.DASHBOARD.MANUFACTURES, icon: Factory },
  { label: "Customers", href: ROUTES.DASHBOARD.CUSTOMERS, icon: Users },
  { label: "Payouts", href: ROUTES.DASHBOARD.PAYOUTS, icon: DollarSign },
  { label: "CMS", href: ROUTES.DASHBOARD.CMS, icon: Cog },
  {
    label: "Settings",
    icon: Settings,
    submenu: [
      { label: "Account Settings", href: ROUTES.DASHBOARD.SETTINGS_ACCOUNT },
      { label: "Pricing", href: ROUTES.DASHBOARD.SETTINGS_PRICING },
      {
        label: "Broadcast Management",
        href: ROUTES.DASHBOARD.BROADCAST_MANAGEMENT,
      },
    ],
  },
];

function NavList({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  return (
    <nav className="space-y-2">
      {navItems.map(({ label, href, icon: Icon, submenu }) => {
        const isActive = href
          ? location.pathname === href ||
            location.pathname.startsWith(href + "/")
          : false;
        const isSubmenuActive = submenu?.some((item) => {
          // Check if current path matches the submenu item
          const directMatch =
            location.pathname === item.href ||
            location.pathname.startsWith(item.href + "/");

          // Special case: if submenu is broadcast-management, also match /broadcast-management routes
          if (item.href === ROUTES.DASHBOARD.BROADCAST_MANAGEMENT) {
            return (
              directMatch ||
              location.pathname.startsWith("/broadcast-management")
            );
          }

          return directMatch;
        });
        const isExpanded = expandedMenu === label;

        if (submenu) {
          return (
            <div key={label}>
              <button
                onClick={() => setExpandedMenu(isExpanded ? null : label)}
                title={collapsed ? label : undefined}
                className={cn(
                  "flex items-center gap-3 px-5 py-3 h-12 md:h-[59px] rounded-lg text-base font-medium transition-colors w-full",
                  isSubmenuActive
                    ? "bg-foreground text-background"
                    : "text-foreground/70 hover:bg-foreground/10 hover:text-foreground",
                  collapsed && "justify-center px-0",
                )}
              >
                <Icon size={18} className="shrink-0" />
                {!collapsed && (
                  <>
                    <span className="whitespace-nowrap truncate flex-1 text-left">
                      {label}
                    </span>
                    <ChevronRight
                      size={16}
                      className={cn(
                        "shrink-0 transition-transform",
                        isExpanded && "rotate-90",
                      )}
                    />
                  </>
                )}
              </button>

              {!collapsed && isExpanded && (
                <div className="mt-1 ml-4 space-y-1 border-l border-border-light pl-3">
                  {submenu.map((item) => {
                    // Check if current path matches the submenu item
                    let isSubitemActive = location.pathname === item.href;

                    // Special case: if submenu is broadcast-management, also match /broadcast-management routes
                    if (item.href === ROUTES.DASHBOARD.BROADCAST_MANAGEMENT) {
                      isSubitemActive =
                        isSubitemActive ||
                        location.pathname.startsWith("/broadcast-management");
                    }

                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={onNavigate}
                        className={cn(
                          "flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                          isSubitemActive
                            ? "bg-foreground text-background"
                            : "text-foreground/70 hover:bg-foreground/10 hover:text-foreground",
                        )}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        return (
          <Link
            key={href}
            to={href!}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={cn(
              "flex items-center gap-3 px-5 py-3 h-12 md:h-[59px] rounded-lg text-base font-medium transition-colors",
              isActive
                ? "bg-foreground text-background"
                : "text-foreground/70 hover:bg-foreground/10 hover:text-foreground",
              collapsed && "justify-center px-0",
            )}
          >
            <Icon size={18} className="shrink-0" />
            {!collapsed && (
              <span className="whitespace-nowrap truncate">{label}</span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

function LogOutItem({ collapsed }: { collapsed: boolean }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);

  const handleConfirmLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      logout();
      navigate(ROUTES.AUTH.LOGIN);
    }
  };

  return (
    <>
      <div className="sticky bottom-0 z-10 bg-background">
        <button
          onClick={() => setOpen(true)}
          title={collapsed ? "Log Out" : undefined}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-base border border-destructive h-12 md:h-[59px] font-medium text-destructive hover:bg-destructive/10 transition-colors w-full",
            collapsed && "justify-center px-0",
          )}
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span>Log Out</span>}
        </button>
      </div>

      <LogoutDialog
        open={open}
        onOpenChange={setOpen}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
}

function DesktopSidebar() {
  const { collapsed, setCollapsed, setCollapsedByBtn, collapsedByBtn } =
    useSidebar();

  // Show expanded if: not collapsed OR (collapsed but hovering)
  const isExpanded = !collapsed;

  const handleToggle = () => {
    if (collapsedByBtn) {
      if (!collapsed) {
        setCollapsed(false);
        setCollapsedByBtn(false);
      } else {
        setCollapsed(false);
        setCollapsedByBtn(false);
      }
    } else {
      setCollapsed(true);
      setCollapsedByBtn(true);
    }
  };

  return (
    <aside
      className={cn(
        "h-full flex flex-col shrink-0 border border-border-light bg-background relative transition-[width] duration-300 ease-in-out rounded-lg",
      )}
    >
      {/* Logo area */}
      <div
        className={cn(
          "flex items-center h-[90px] p-2 border-b border-border-light px-4 overflow-hidden relative",
          isExpanded ? "justify-start" : "justify-center px-0",
        )}
      >
        <Link to={ROUTES.DASHBOARD.HOME} className="shrink-0">
          <Logo isCollapsed={!isExpanded} />
        </Link>

        {/* Collapse toggle — only show when expanded */}

        <button
          onClick={handleToggle}
          aria-label="Collapse sidebar"
          className={`absolute right-3 top-1/2 -translate-y-1/2 z-20 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background shadow-sm hover:bg-accent transition-all duration-300 cursor-pointer ${collapsedByBtn ? "rotate-180" : ""} ${isExpanded ? "" : "opacity-0"}`}
        >
          <ChevronRight size={12} />
        </button>
      </div>

      <div
        className={
          "flex flex-col justify-between py-5 pl-5 pr-2 h-[calc(100%-90px)] overflow-auto"
        }
      >
        {/* Nav */}
        <div className="w-full pb-3">
          <NavList collapsed={!isExpanded} />
        </div>

        {/* Log out */}
        <div className="w-full">
          <LogOutItem collapsed={!isExpanded} />
        </div>
      </div>
    </aside>
  );
}

function MobileDrawer() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        className="lg:hidden flex items-center justify-center h-9 w-9 rounded-lg border border-border hover:bg-accent transition-colors"
      >
        <Menu size={18} />
      </button>

      <AppDrawer
        open={open}
        onClose={() => setOpen(false)}
        direction="left"
        size={320}
      >
        <button
          onClick={() => setOpen(false)}
          aria-label="Close menu"
          className="flex items-center justify-center h-9 w-9 hover:bg-accent transition-colors ml-2 shrink-0 cursor-pointer absolute right-1 top-1 bg-background border border-border rounded-full p-1"
        >
          <X size={18} />
        </button>
        <div className="px-4 py-3 overflow-auto">
          <div className="border border-border-light rounded-lg">
            <div className="flex items-center justify-center h-auto md:h-[90px] border-b border-border-light shrink-0 p-3">
              <Link to={ROUTES.DASHBOARD.HOME} onClick={() => setOpen(false)}>
                <Logo isCollapsed={false} />
              </Link>
            </div>

            <div className="h-[calc(100dvh-120px)] overflow-auto flex flex-col justify-between py-5 px-3">
              <div className="w-full pb-3">
                <NavList collapsed={false} onNavigate={() => setOpen(false)} />
              </div>

              <div className="w-full">
                <LogOutItem collapsed={false} />
              </div>
            </div>
          </div>
        </div>
      </AppDrawer>
    </>
  );
}

export { DesktopSidebar, MobileDrawer };
